/* ============================================
   PRTCL (formerly PenRx) Shared Component Styles
   ============================================ */

/* ---- Button ---- */
.btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: var(--space-2);
  font-weight: var(--font-weight-semibold);
  border-radius: var(--radius-md);
  transition: all var(--transition-fast);
  cursor: pointer;
  white-space: nowrap;
  position: relative;
  -webkit-tap-highlight-color: transparent;
  min-height: 44px;
  min-width: 44px;
}

.btn:active {
  transform: scale(0.97);
}

.btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
  transform: none;
}

.btn-sm { padding: var(--space-2) var(--space-4); font-size: var(--font-size-sm); min-height: var(--tap-target-min); }
.btn-md { padding: var(--space-3) var(--space-6); font-size: var(--font-size-base); }
.btn-lg { padding: var(--space-4) var(--space-8); font-size: var(--font-size-md); min-height: 52px; }

.btn-primary {
  background: var(--color-accent);
  color: var(--color-text-inverse);
  font-size: var(--font-size-sm);
}
.btn-primary:hover:not(:disabled) {
  background: var(--color-accent-hover);
  box-shadow: 0 4px 14px var(--color-accent-shadow);
}

.btn-secondary {
  background: transparent;
  color: var(--color-text-primary);
  /* Border uses text-tertiary (medium grey) instead of color-border-hover
     (0.15 alpha) so the button doesn't read as disabled - finding #42. */
  border: 1px solid var(--color-text-tertiary);
  font-size: var(--font-size-sm);
}
.btn-secondary:hover:not(:disabled) {
  background: var(--color-bg-hover);
  border-color: var(--color-text-secondary);
}

.btn-ghost {
  background: transparent;
  color: var(--color-accent);
}
.btn-ghost:hover:not(:disabled) {
  background: var(--color-accent-muted);
}

.btn-danger {
  background: var(--color-error-bg);
  color: var(--color-error);
}
.btn-danger:hover:not(:disabled) {
  background: var(--color-error-bg-hover);
}

.btn-full { width: 100%; }

.btn-loading {
  color: transparent !important;
}

/* Spinner colors are set explicitly per button variant rather than using
   currentColor, because .btn-loading sets color:transparent!important on
   the button to hide its text while the spinner overlays it - which would
   otherwise cascade into the spinner's border-top-color as transparent. */
.btn-spinner {
  position: absolute;
  width: 18px;
  height: 18px;
  border: 2px solid rgba(255, 255, 255, 0.25);
  border-top-color: var(--color-text-inverse);
  border-radius: 50%;
  animation: spin 0.6s linear infinite;
}

.btn-secondary .btn-spinner,
.btn-ghost .btn-spinner {
  border: 2px solid rgba(0, 0, 0, 0.15);
  border-top-color: var(--color-text-primary);
}

.btn-danger .btn-spinner {
  border: 2px solid rgba(239, 68, 68, 0.2);
  border-top-color: var(--color-error);
}

.btn-icon {
  display: flex;
  align-items: center;
  font-size: 1.1em;
}

/* ---- Card ---- */
.card {
  background: var(--color-bg-secondary);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-lg);
  transition: all var(--transition-base);
}

.card-pad-sm { padding: var(--space-3); }
.card-pad-md { padding: var(--space-5); }
.card-pad-lg { padding: var(--space-6); }

.card-glass {
  background: var(--glass-bg);
  backdrop-filter: blur(var(--glass-blur));
  -webkit-backdrop-filter: blur(var(--glass-blur));
  border-color: var(--glass-border);
}

.card-glow {
  box-shadow: var(--shadow-glow-sm);
  border-color: var(--color-border-accent);
}

.card-interactive {
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;
}

.card-interactive:hover {
  background: var(--color-bg-tertiary);
  border-color: var(--color-border-hover);
  transform: translateY(-1px);
  box-shadow: var(--shadow-md);
}

.card-interactive:active {
  transform: scale(0.99) translateY(0);
}

/* ---- Input ---- */
.input-group {
  display: flex;
  flex-direction: column;
  gap: var(--space-2); /* 8px - clean rhythm, no negative-margin corrections */
}

/* Breathing room between adjacent input groups so labels never hug
   the previous field. 32px works across all current screens. */
.input-group + .input-group {
  margin-top: var(--space-8);
}

.input-label {
  font-family: var(--font-ui-label);
  /* Micro-caption sizing. Labels previously used --font-size-sm (14px) which
     read as competitive with body copy; shrinking to --font-size-xs (12px)
     makes them clearly secondary - the pattern for uppercase form labels
     across the app. */
  font-size: var(--font-size-xs);
  font-weight: var(--font-weight-bold);
  color: var(--color-text-tertiary);
  letter-spacing: var(--letter-spacing-wide);
  text-transform: uppercase;
}

.input-label-row {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: var(--space-3);
}

.input-label-action {
  font-size: var(--font-size-xs);
  font-weight: var(--font-weight-medium);
  color: var(--color-accent);
}

.input-label-action a,
.input-label-action button {
  color: inherit;
  text-decoration: none;
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  font: inherit;
}

.input-label-action a:hover,
.input-label-action button:hover {
  color: var(--color-accent-hover);
}

.input-description {
  font-size: var(--font-size-sm);
  color: var(--color-text-tertiary);
  line-height: var(--line-height-normal); /* 1.47, tighter rhythm under bold label */
}

.input-wrapper {
  display: flex;
  align-items: center;
  background: var(--color-bg-tertiary);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-md);
  padding: 0 var(--space-4);
  min-height: 48px;
  transition: border-color var(--transition-fast);
  max-width: 100%;
  overflow: hidden;
  outline: none;
}

.input-wrapper *:focus {
  outline: none;
}

.input-wrapper:focus-within {
  border-color: var(--color-accent);
}

.input-wrapper:focus-within .input-field,
.input-wrapper .input-field:focus {
  border: none;
  outline: none;
  box-shadow: none;
  -webkit-appearance: none;
}

.input-error .input-wrapper {
  border-color: var(--color-error);
}

.input-error .input-wrapper:focus-within {
  border-color: var(--color-error);
}

.input-icon {
  display: flex;
  align-items: center;
  color: var(--color-text-tertiary);
  margin-right: var(--space-3);
}

.input-field {
  flex: 1;
  padding: var(--space-3) 0;
  font-size: 16px; /* Prevents iOS Safari auto-zoom on focus */
  color: var(--color-text-primary);
  background: transparent;
  min-width: 0;
  border: none;
  outline: none;
}

.input-field::placeholder {
  color: var(--color-text-tertiary);
}

.input-field::-webkit-outer-spin-button,
.input-field::-webkit-inner-spin-button {
  -webkit-appearance: none;
  margin: 0;
}

.input-field[type=number] {
  -moz-appearance: textfield;
}

.input-suffix {
  font-size: var(--font-size-sm);
  color: var(--color-text-tertiary);
  margin-left: var(--space-2);
  font-weight: var(--font-weight-medium);
  display: flex;
  align-items: center;
}

.input-suffix button {
  background: none;
  border: none;
  /* Extra horizontal padding so the icon has breathing room against
     the wrapper's right edge, fixing the cramped password toggle on
     mobile (finding #43). */
  padding: var(--space-1) var(--space-2);
  cursor: pointer;
  color: var(--color-text-tertiary);
  display: flex;
  align-items: center;
  transition: color var(--transition-fast);
}

.input-suffix button:hover {
  color: var(--color-text-secondary);
}

.input-error-text {
  font-size: var(--font-size-xs);
  color: var(--color-error);
}

.input-helper {
  font-size: var(--font-size-xs);
  color: var(--color-text-tertiary);
  line-height: var(--line-height-relaxed);
}

/* ---- Select ---- */
.select-field {
  appearance: none;
  -webkit-appearance: none;
  cursor: pointer;
  padding-right: var(--space-8);
  width: 100%;
  outline: none;
}

.select-field option {
  background: var(--color-bg-primary);
  color: var(--color-text-primary);
}

.select-arrow {
  display: flex;
  align-items: center;
  color: var(--color-text-tertiary);
  pointer-events: none;
  position: absolute;
  right: var(--space-4);
}

.input-wrapper {
  position: relative;
}

/* ---- Badge ---- */
.badge {
  display: inline-flex;
  align-items: center;
  font-family: var(--font-ui-label);
  font-weight: var(--font-weight-semibold);
  border-radius: var(--radius-full);
  white-space: nowrap;
  letter-spacing: var(--letter-spacing-wide);
  text-transform: uppercase;
}

.badge-sm { padding: 2px var(--space-2); font-size: var(--font-size-xs); }
.badge-md { padding: var(--space-1) var(--space-3); font-size: var(--font-size-xs); }

.badge-default { background: var(--color-bg-elevated); color: var(--color-text-secondary); }
.badge-success { background: var(--color-success-bg); color: var(--color-success); }
.badge-warning { background: var(--color-warning-bg); color: var(--color-warning); }
.badge-error { background: var(--color-error-bg); color: var(--color-error); }
.badge-info { background: var(--color-accent-muted); color: var(--color-accent); }
.badge-accent { background: var(--color-accent-muted); color: var(--color-accent); }

/* ---- Modal / Bottom Sheet ---- */
.modal-overlay {
  position: fixed;
  inset: 0;
  background: var(--overlay-bg);
  z-index: var(--z-modal);
  display: flex;
  align-items: flex-end;
  justify-content: center;
  animation: fadeIn 200ms ease both;
  height: 100%;
  height: 100dvh;
}

.modal-sheet {
  background: var(--color-bg-secondary);
  border-top: 1px solid var(--color-border-hover);
  border-radius: var(--radius-2xl) var(--radius-2xl) 0 0;
  width: 100%;
  max-width: 600px;
  max-height: 90vh;
  max-height: 90dvh;
  overflow-y: auto;
  padding: var(--space-4) var(--space-6) var(--space-8);
  padding-bottom: calc(var(--space-8) + env(safe-area-inset-bottom, 0px));
  /* Smooth ride when --keyboard-height lifts the sheet (rule below). */
  transition: margin-bottom 250ms ease;
}

/* When the iOS keyboard is open, lift the whole bottom sheet above it.
   The scroll-the-container fix (9aa0bc56) only helps TALL sheets that
   have internal scroll room; a short sheet (e.g. the Start Using modal,
   one input) has nothing to scroll, so the sheet itself must move.
   --keyboard-height + data-keyboard-open are maintained by useKeyboard
   on keyboardWillShow/Hide - same pattern as the page padding in
   globals.css. Max height shrinks too so tall sheets stay fully
   visible (their internal scroll handles the rest). */
html[data-keyboard-open] .modal-sheet {
  margin-bottom: var(--keyboard-height, 0px);
  max-height: calc(100dvh - var(--keyboard-height, 0px) - var(--space-6));
}

.modal-handle {
  width: 36px;
  height: 4px;
  /* Use border token instead of text-tertiary so the handle recedes to
     a hint in dark themes where text-tertiary is bright (finding #31). */
  background: var(--color-border-hover);
  border-radius: var(--radius-full);
  margin: 0 auto var(--space-5);
}

.modal-title {
  font-size: var(--font-size-lg);
  font-weight: var(--font-weight-bold);
  margin-bottom: var(--space-5);
}

/* Toolbar header: X | Title | Done */
.modal-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin: calc(-1 * var(--space-2)) 0 var(--space-4);
}

.modal-toolbar-close {
  width: var(--tap-target-min);
  height: var(--tap-target-min);
  border-radius: var(--radius-md);
  background: var(--color-bg-tertiary);
  border: none;
  color: var(--color-text-primary);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: background var(--transition-fast);
  flex-shrink: 0;
}

.modal-toolbar-close:hover {
  background: var(--color-border);
}

.modal-toolbar-title {
  font-size: var(--font-size-base);
  font-weight: var(--font-weight-semibold);
  color: var(--color-text-primary);
  text-align: center;
  flex: 1;
  margin: 0;
}

.modal-toolbar-done {
  background: none;
  border: none;
  font-size: var(--font-size-base);
  font-weight: var(--font-weight-semibold);
  color: var(--color-accent);
  cursor: pointer;
  padding: var(--space-2) var(--space-3);
  min-height: var(--tap-target-min);
  min-width: var(--tap-target-min);
  transition: opacity var(--transition-fast);
  flex-shrink: 0;
}

.modal-toolbar-done:hover {
  opacity: 0.8;
}

.modal-toolbar-done-disabled,
.modal-toolbar-done:disabled {
  color: var(--color-text-tertiary);
  cursor: not-allowed;
  opacity: 0.5;
}

.modal-content {
  display: flex;
  flex-direction: column;
  gap: var(--space-5);
}

/* ---- Empty State ---- */
.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  padding: var(--space-12) var(--space-6);
  gap: var(--space-4);
}

.empty-state-icon {
  /* Bumped from 2.5rem (40px) to 3rem (48px) so inline-icon empty states
     align visually with the dashboard's 48px SVG-in-circle treatment
     (finding #30). */
  font-size: 3rem;
  opacity: 0.5;
  margin-bottom: var(--space-2);
}

.empty-state-title {
  font-size: var(--font-size-lg);
  font-weight: var(--font-weight-semibold);
  color: var(--color-text-primary);
}

.empty-state-desc {
  font-size: var(--font-size-base);
  color: var(--color-text-secondary);
  max-width: 300px;
}

.empty-state-action {
  margin-top: var(--space-4);
}

/* ---- Progress Bar ---- */
.progress-bar {
  width: 100%;
  background: var(--color-bg-tertiary);
  border-radius: var(--radius-full);
  overflow: hidden;
}

.progress-bar-sm { height: 4px; }
.progress-bar-md { height: 6px; }

.progress-bar-fill {
  height: 100%;
  background: linear-gradient(90deg, var(--color-accent), var(--color-progress-end));
  border-radius: var(--radius-full);
  transition: width var(--transition-slow);
}

/* ---- Vial Size Row (shared across calculator, protocols, inventory) ---- */

.calc-vial-row {
  display: flex;
  gap: var(--space-3);
  align-items: flex-end;
}

.calc-vial-input {
  flex: 1;
  min-width: 0;
}

.calc-vial-unit {
  width: 100px;
  flex-shrink: 0;
}

/* ---- Dose Row (shared: Input + Unit Selector) ----
   We want the INPUT FIELD and the DROPDOWN FIELD to sit on the same
   baseline, regardless of whether the Input carries a description row
   that makes its overall block taller than the Select.
   Strategy: let the row stretch both columns to the same (max) height,
   then flip each column into a vertical flex container that pins its
   child (the Input/Select's own .input-group) to the bottom. The
   label/description stack stays on top and flows upward; the actual
   field box sits on the shared bottom baseline. */

.dose-row {
  display: flex;
  gap: var(--space-3);
  align-items: stretch;
}

.dose-input,
.dose-unit {
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  min-width: 0;
}

.dose-input { flex: 2; }
.dose-unit { flex: 1; }

/* Legacy aliases - same behavior as above */
.protocol-dose-row {
  display: flex;
  gap: var(--space-3);
  align-items: stretch;
  /* Small top buffer so the form doesn't scrunch against the progress
     bar at the top of the wizard step. */
  margin-top: var(--space-3);
}
.protocol-dose-input,
.protocol-dose-unit {
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  min-width: 0;
}
.protocol-dose-input { flex: 2; }
.protocol-dose-unit { flex: 1; }

/* Helper text rendered below a dose row (Protocol wizard Dose step).
   Matches the Input's description styling (font-size-sm, tertiary color)
   for consistency with other forms. */
.protocol-dose-helper {
  margin-top: var(--space-1);
  font-size: var(--font-size-sm);
  color: var(--color-text-tertiary);
  line-height: var(--line-height-normal);
}

/* ---- Dosing Disclaimer ---- */

.dosing-disclaimer {
  display: flex;
  align-items: flex-start;
  gap: var(--space-3);
  font-size: var(--font-size-sm);
  line-height: var(--line-height-relaxed);
  color: var(--color-text-tertiary);
}

.dosing-disclaimer strong {
  color: var(--color-text-secondary);
  font-weight: var(--font-weight-semibold);
}

.dosing-disclaimer-icon {
  flex-shrink: 0;
  margin-top: 1px;
  color: var(--color-warning);
}

.dosing-disclaimer-inline {
  padding: var(--space-3) var(--space-4);
  background: var(--color-warning-bg);
  border: 1px solid var(--color-warning-border);
  border-radius: var(--radius-md);
  margin-bottom: var(--space-4);
}

.dosing-disclaimer-banner {
  padding: var(--space-4) var(--space-5);
  background: var(--color-warning-bg);
  border: 1px solid var(--color-warning-border);
  border-radius: var(--radius-lg);
  margin-bottom: var(--space-5);
}
